import { useEffect, useMemo, useState } from 'react';
import { useTestCaseContext } from '@Context/TestCaseContext';
import {
  GeneratedHistory,
  PushHistory,
  TestCaseProp,
  TestCases,
} from '@Models/types';
import {
  MagnifyingGlass,
  SquaresFour,
  X,
  CaretUp,
  CaretDoubleDown,
} from '@phosphor-icons/react';
import { parseLinks } from '@Generator/history/PubColumn';
import { useQueueContext } from '@Context/QueueContext';
import colors from '@Common/assets/colors';
import Modal from '@Common/Modal';
import Projects from './Projects';
import Sections from './Sections';
import TestCaseList from './TestCaseList';
import Links from '../Links';
import { MoonLoader } from 'react-spinners';
import RevisionHistory from './RevisionHistory';
import Instructions from './Instructions';
import TestCase from '../TestCase';

const GeneratedView = () => {
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedRefNum, setSelectedRefNum] = useState<string | undefined>(
    undefined
  );
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<TestCases[]>([]);
  const [selectedTestCase, setSelectedTestCase] = useState<TestCaseProp | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [instructionModal, setInstructionModal] = useState(false);
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false);
  const { selectedGenerated, setSelectedGenerated, pushToTestRail } =
    useTestCaseContext();
  const { addToQueue, queue, isInProgress, setQueue } = useQueueContext();
  const [search, setSearch] = useState('');
  const [generatedTestCases, setGeneratedTestCases] = useState<TestCases[]>([]);

  useEffect(() => {
    if (selectedTestCase) {
      const selectedTest = generatedTestCases.find(
        (genTest) => genTest.reference_num === selectedRefNum
      );
      if (selectedTest) {
        const index = selectedTest.GeneratedTestCase.findIndex(
          (exTitle) => exTitle.title === selectedTestCase.title
        );
        const generated = generatedTestCases.map((item) => {
          if (item.reference_num == selectedRefNum) {
            const newTestCase = item.GeneratedTestCase.map(
              (testCase, testIndex) => {
                if (testIndex === index) {
                  setSelected((prev) => {
                    return prev.map((testCase) => {
                      if (testCase.reference_num === selectedRefNum) {
                        return {
                          ...testCase,
                          GeneratedTestCase: testCase.GeneratedTestCase.map(
                            (gen) => {
                              if (gen.title === selectedTestCase.title) {
                                return selectedTestCase;
                              }
                              return gen;
                            }
                          ),
                        };
                      }
                      return testCase;
                    });
                  });
                  return selectedTestCase;
                } else {
                  return testCase;
                }
              }
            );
            return {
              ...item,
              GeneratedTestCase: newTestCase,
            };
          } else {
            return item;
          }
        });
        if (selectedGenerated) {
          selectedGenerated.completion_result = JSON.stringify(generated);
          setSelectedGenerated(selectedGenerated);
          setGeneratedTestCases(generated);
        }
      }
    }
  }, [selectedTestCase]);

  useEffect(() => {
    if (selected.length > 0) {
      setError(null);
    }
  }, [selected]);

  useEffect(() => {
    const isQueueDone = queue.every((item) => !isInProgress(item));
    if (!loading && isQueueDone) {
      setBottomPanelOpen(false);
    }
  }, [loading, queue]);

  const getCompletionResults = (
    generatedHistory: GeneratedHistory
  ): TestCases[] => {
    const completionResult = JSON.parse(generatedHistory?.completion_result);

    const filtered = completionResult
      .map((item: { GeneratedTestCase: { title: string }[] }) => ({
        ...item,
        GeneratedTestCase: item.GeneratedTestCase,
      }))
      .filter(
        (item: { GeneratedTestCase: { title: string }[] }) =>
          item.GeneratedTestCase.length > 0
      );
    return filtered;
  };

  const filteredArray = useMemo(() => {
    return generatedTestCases.filter((item) => {
      return (
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.reference_num.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [search, selectedGenerated, generatedTestCases]);

  useEffect(() => {
    if (selectedGenerated) {
      const filtered = getCompletionResults(selectedGenerated);
      setGeneratedTestCases(filtered);
    }
    setSelectedTestCase(null);
    setSelected([]);
  }, [selectedGenerated]);

  if (!selectedGenerated) {
    return <div></div>;
  }

  const handlePush = async () => {
    const filterQueue = queue.filter(
      (item): item is PushHistory =>
        'push_status' in item && item.generate_id !== selectedGenerated.id,
      setBottomPanelOpen(false)
    );

    setQueue(filterQueue);

    if (selected.length === 0) {
      setError('Select at least one test case');
    } else {
      setLoading(true);
      setError(null);
      setSelected([]);
      const queue = await pushToTestRail(selected);
      await addToQueue(queue.queueId, true).then(() => setLoading(false));
    }
  };

  return (
    <div className="flex h-full w-full flex-col md:flex-row">
      <div className="flex h-full flex-col border-b-2 p-4 md:w-1/3 md:border-r-2">
        <div className="flex flex-col gap-y-2">
          <label className="font-inter text-xs font-bold text-gray-500">
            Generated Test Cases
          </label>
        </div>
        <div className="mt-1 flex flex-col items-start justify-between md:flex-row md:items-center">
          <label className="text-xl font-bold text-primary-700">
            Test Cases
          </label>
          <label className="flex flex-row gap-x-2">
            <button
              onClick={() => setInstructionModal(true)}
              className="rounded-md bg-primary-700 px-4 py-2 text-xs text-white"
            >
              Regenerate
            </button>

            <button
              onClick={() => setModalOpen(true)}
              className="rounded-md bg-primary-700 px-4 py-2 text-xs text-white"
            >
              History
            </button>
          </label>
        </div>
        <div className="mt-2 flex flex-row items-center gap-x-2 rounded-md border px-4 py-2">
          <MagnifyingGlass
            size={20}
            weight="bold"
            className="text-primary-700"
          />
          <input
            type="text"
            className="w-full border-gray-300 font-inter text-sm focus:outline-none"
            placeholder="Search Test Cases"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <X
            weight="bold"
            className="cursor-pointer hover:text-brandRed-500"
            onClick={() => setSearch('')}
          />
        </div>

        <TestCaseList
          viewedTestcase={selectedTestCase}
          selected={selected}
          setSelected={setSelected}
          setSelectedTestCase={(
            testCase: TestCaseProp,
            index: number,
            refNum?: string
          ) => {
            setSelectedTestCase(testCase);
            setSelectedIndex(index);
            selectedIndex;
            setSelectedRefNum(refNum);
            if (!bottomPanelOpen) {
              setBottomPanelOpen(true);
            }
          }}
          filteredArray={filteredArray}
        />
      </div>

      <div
        className={`absolute bottom-0 right-0 z-50 flex h-1/2 w-full transform flex-col items-center transition-transform md:h-2/3 md:w-2/3 ${bottomPanelOpen ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <button
          className={`${bottomPanelOpen ? '-top-11 h-[50px] w-[60px] rounded-t-[10px]' : '-top-7 h-[20px] w-[50px] rounded-t-[10px] py-4'} absolute flex items-center justify-center border border-primary-600 bg-primary-600 p-2 text-white shadow-lg transition-transform duration-1000`}
          onClick={() => setBottomPanelOpen(!bottomPanelOpen)}
          aria-label="Toggle Bottom Panel"
        >
          {bottomPanelOpen ? (
            <CaretDoubleDown size={30} />
          ) : (
            <CaretUp size={30} />
          )}
        </button>
        {bottomPanelOpen && (
          <div className="h-full w-full border-t-8 border-primary-600 bg-white p-4 shadow-lg">
            <label className="font-inter text-xs font-bold text-gray-500">
              Test Case Details
            </label>
            {!selectedTestCase && (
              <div className="flex h-full w-full flex-col items-center justify-center">
                <SquaresFour size={200} className="text-gray-300" />
                <label className="font-semibold text-gray-500">
                  Select a Test Case first
                </label>
              </div>
            )}
            {selectedTestCase && (
              <TestCase
                testcase={selectedTestCase}
                setSelectedTestCase={setSelectedTestCase}
                isEditable={true}
              />
            )}
          </div>
        )}
      </div>

      <div className="custom-scroll flex h-full w-full flex-col overflow-y-scroll md:w-2/3">
        <div className="w-full p-4">
          <div className="flex flex-col gap-y-2">
            <label className="font-inter text-xs font-bold text-gray-500">
              TestRail Publication
            </label>
          </div>

          <Projects />
          <Sections />

          <div className="mt-4 flex flex-col items-start md:mt-0 md:items-end md:p-4">
            <div className="flex flex-col gap-y-2 md:items-center">
              <button
                onClick={handlePush}
                className="rounded-md bg-primary-700 px-4 py-2 text-white"
              >
                Publish Test Cases
              </button>
              {error && (
                <span className="animate-pulse text-xs font-bold text-brandRed-500">
                  {error}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={`relative h-full w-full flex-col`}>
          <div className={`absolute left-0 top-0 h-full w-full p-4`}>
            {loading && (
              <div className="flex w-full flex-col items-center justify-center pb-5">
                <MoonLoader size={100} color={colors.primary[700]} />
                <label className="mb-5 mt-2 animate-pulse pb-5">
                  Pushing to TestRail
                </label>
              </div>
            )}

            {!loading &&
              queue.length > 0 &&
              queue
                .filter(
                  (item): item is PushHistory =>
                    'push_status' in item &&
                    item.generate_id === selectedGenerated.id
                )
                .map((q) => (
                  <>
                    {!isInProgress(q) && (
                      <Links key={q.id} links={parseLinks(q.push_result)} />
                    )}
                    {isInProgress(q) && (
                      <div
                        key={q.id}
                        className="flex w-full flex-col items-center justify-center"
                      >
                        <MoonLoader size={100} color={colors.primary[700]} />
                        <label className="mb-5 mt-2 animate-pulse pb-5">
                          Pushing to TestRail
                        </label>
                      </div>
                    )}
                  </>
                ))}
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Revision History"
      >
        <RevisionHistory id={selectedGenerated.id} />
      </Modal>
      <Modal
        isOpen={instructionModal}
        onClose={() => setInstructionModal(false)}
        title="Instructions"
      >
        <Instructions onClose={() => setInstructionModal(false)} />
      </Modal>
    </div>
  );
};

export default GeneratedView;
